/** Parts the expansion's models share (10 §3): wheels, tubes lying down, struts between two points, snow patches. */

import type { Color } from 'three';
import type { Palette } from '../palette';
import { type BoxColors, type MeshBuilder, place } from './MeshBuilder';

export type Point = readonly [x: number, y: number, z: number];

/**
 * A ramp's mid tone with its dark step on the east and west faces, so the corner they make with the
 * front reads (the rule buildings.ts states); `top` overrides the top face - snow, a lighter step.
 */
export function sided(p: Palette, ramp: string, top?: Color): BoxColors {
  const lo = p.color(`${ramp}.lo`);
  return { all: p.color(`${ramp}.mid`), left: lo, right: lo, ...(top ? { top } : {}) };
}

/**
 * A road or rail wheel standing on its tread: axle along x, hub at (x, y, z), `width` across the tread.
 * The face colour is the cap, so a loco can have red wheel centres and a truck grey hubs.
 */
export function wheel(
  mb: MeshBuilder, p: Palette, at: Point, r: number, width: number, sides = 6, hub = 'metal.lo',
): void {
  // Rolled a quarter turn a cylinder's +y runs along -x, so it starts half a width to the +x side.
  const m = place(at[0] + width / 2, at[1], at[2], 0, 1, 0, Math.PI / 2);
  mb.cylinder(r, r, width, sides, p.color('ink.1'), m, p.color(hub));
}

/** A tube lying along z from `z0` to the greater `z1`, its axis at (x, y); `r0` is the radius at `z0`. */
export function tubeZ(
  mb: MeshBuilder, x: number, y: number, z0: number, z1: number, r0: number, r1: number, sides: number,
  color: Color, cap?: Color,
): void {
  mb.cylinder(r0, r1, z1 - z0, sides, color, place(x, y, z0, 0, 1, Math.PI / 2), cap);
}

/** A square bar from `a` to `b`: a guy wire, a trail leg, a tripod leg, a lattice brace. */
export function strut(mb: MeshBuilder, a: Point, b: Point, thickness: number, color: Color): void {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  const length = Math.hypot(dx, dy, dz);
  if (length < 1e-6) return;
  // Yaw turns the box's long (z) axis to the heading; pitch about x then lifts or drops its far end.
  const pitch = -Math.asin(dy / length);
  const m = place((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2, Math.atan2(dx, dz), 1, pitch);
  mb.box(thickness, thickness, length, color, m, true);
}

/**
 * Arctic only: one small inset drift lying on a top at height `y`. Roofs big enough for three drifts
 * use `snowSlab`; this is for a cab roof, a tarpaulin, a bonnet - always smaller than the top it lies on.
 */
export function snowPatch(mb: MeshBuilder, p: Palette, x: number, y: number, z: number, w: number, d: number): void {
  if (p.biome !== 'arctic') return;
  mb.box(w, 0.1, d, { all: p.color('ground.2'), top: p.color('ground.3') }, place(x, y, z));
}
